import React from 'react';
import Icon from './icons';
import { useSch } from './context';

/** Target chips: all screens, each group, and a one-screen picker (the prototype's `targets()`). */
export function Targets({ target, onTarget }) {
  const { t, f, groups, screens } = useSch();
  const one = screens.find((s) => s.id === target);
  return (
    <div className="sch6-targets" role="group" aria-label={t('sch6.screens_aria')}>
      <button type="button" className="sch6-tchip" aria-pressed={target === 'all'} onClick={() => onTarget('all')}>
        {`${t('sch6.all_screens')} `}<small>{f.num(screens.length)}</small>
      </button>
      {groups.filter((g) => g.id !== null).map((g) => (
        <button type="button" key={g.id} className="sch6-tchip" aria-pressed={target === g.id} onClick={() => onTarget(g.id)}>
          {`${g.name} `}<small>{f.num(g.screens.length)}</small>
        </button>
      ))}
      <select className="sch6-pickscreen" aria-label={t('sch6.one_screen_aria')} value={one ? target : ''} onChange={(e) => { if (e.target.value) onTarget(e.target.value); }}>
        <option value="">{one ? one.name : t('sch6.one_screen')}</option>
        {groups.map((g) => (
          <optgroup key={g.id ?? 'none'} label={g.name ?? t('sch6.ungrouped')}>
            {g.screens.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </optgroup>
        ))}
      </select>
    </div>
  );
}

/** "Outside the scheduled times" card: the show the screens go back to (the most common standing show). */
export function DefCard({ def }) {
  const { t, f, showName, col } = useSch();
  return (
    <section className="sch6-card sch6-def" aria-label={t('sch6.def_aria')}>
      <div>
        <div className="sch6-eyebrow">{t('sch6.def_eyebrow')}</div>
        <div className="sch6-showname" style={{ fontSize: 17 }}>
          <span className="sch6-sw" style={{ background: col(def?.show) }} />
          {`${def?.show ? showName(def.show) : t('sch6.def_none')} `}{def?.show && <span className="sch6-badge sch6-mute">{t('sch6.def_badge')}</span>}
          {def?.others > 0 && <span className="sch6-badge sch6-mute">{t('sch6.def_mixed', { n: f.nScreens(def.others) })}</span>}
        </div>
        <div className="sch6-meta">{t('sch6.def_meta')}</div>
      </div>
    </section>
  );
}

/** No rule and no event yet: three ways to start (the prototype's `emptyState()`). */
export function EmptyState({ onQuick, def }) {
  const { t, f } = useSch();
  return (
    <>
      <section className="sch6-card sch6-starter">
        <h2>{t('sch6.starter_title')}</h2>
        <p>{t('sch6.starter_p')}</p>
        <div className="sch6-starts">
          <button type="button" className="sch6-start" onClick={() => onQuick('daily')}>
            <span className="sch6-ic"><Icon name="sun" /></span>
            <b>{t('sch6.q_daily')}</b><span>{t('sch6.q_daily_d', { a: f.tmShort(360), b: f.tmShort(720) })}</span>
          </button>
          <button type="button" className="sch6-start" onClick={() => onQuick('once')}>
            <span className="sch6-ic"><Icon name="clock" /></span>
            <b>{t('sch6.q_once')}</b><span>{t('sch6.q_once_d')}</span>
          </button>
          <button type="button" className="sch6-start" onClick={() => onQuick('event')}>
            <span className="sch6-ic"><Icon name="cal" /></span>
            <b>{t('sch6.q_event')}</b><span>{t('sch6.q_event_d')}</span>
          </button>
        </div>
      </section>
      <DefCard def={def} />
    </>
  );
}

export function Loading() {
  const { t } = useSch();
  return (
    <>
      <div className="sch6-skel" style={{ height: 44, width: '60%' }} />
      <div className="sch6-skel" style={{ height: 120 }} />
      <div className="sch6-skel" style={{ height: 80 }} />
      <div className="sch6-skel" style={{ height: 80 }} />
      <p className="sch6-sub" role="status">{t('sch6.loading')}</p>
    </>
  );
}

export function ErrorState({ onRetry }) {
  const { t } = useSch();
  return (
    <div className="sch6-errbox" role="alert">
      <Icon name="alert" />
      <div>
        <b>{t('sch6.err_title')}</b>
        <span className="sch6-meta">{t('sch6.err_body')}</span>
        <div style={{ marginTop: 10 }}><button type="button" className="sch6-btn" onClick={onRetry}>{t('sch6.retry')}</button></div>
      </div>
    </div>
  );
}
